import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, downloadContractDoc, uploadContractDoc, type ContractDocs } from '../lib/api';
import { dateTime } from '../lib/format';
import { formatBytes } from './Conversation';
import { DownloadIcon, FileIcon, IconButton, TrashIcon } from './Icons';

// Documentos del contrato (PDF o Word): el contrato firmado y sus anexos, de referencia.

export function ContractDocuments({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const key = ['contract-docs', projectId];
  const q = useQuery({ queryKey: key, queryFn: () => api<ContractDocs>(`/contracts/${projectId}/documents`) });
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (fn: () => Promise<ContractDocs>) => {
    setBusy(true); setError('');
    try { queryClient.setQueryData(key, await fn()); } catch (e) { setError(e instanceof ApiError ? e.message : 'No se pudo completar.'); } finally { setBusy(false); }
  };
  const d = q.data;

  return (
    <section className="rounded-lg border border-line bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Documentos del contrato</h2>
          <p className="mt-0.5 text-xs text-muted">El contrato firmado y sus anexos (PDF o Word), para tenerlos a la mano.</p>
        </div>
        {d?.canUpload && d.enabled && (
          <>
            <button type="button" disabled={busy} onClick={() => input.current?.click()}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-[#D6D3CD] bg-white px-3 text-sm hover:bg-paper disabled:opacity-50">
              <FileIcon /> {busy ? 'Subiendo…' : 'Subir documento'}
            </button>
            <input ref={input} type="file" className="hidden" accept=".pdf,.doc,.docx,.odt,.rtf" aria-hidden="true" tabIndex={-1}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void run(() => uploadContractDoc(projectId, f)); }} />
          </>
        )}
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-[#8F1D1D]">{error}</p>}
      {q.isLoading ? <p className="mt-3 text-sm text-muted">Cargando…</p> : !d?.enabled ? (
        <p className="mt-3 text-sm text-muted">El almacenamiento de archivos no está configurado.</p>
      ) : d.documents.length === 0 ? (
        <p className="mt-3 text-sm text-muted">Aún no hay documentos.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[#EFEDE9] text-sm">
          {d.documents.map(doc => (
            <li key={doc.id} className="flex items-center gap-3 py-2">
              <span className="text-muted"><FileIcon /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{doc.fileName}</span>
                <span className="text-xs text-muted">{formatBytes(doc.size)} · {doc.uploadedBy.name} · {dateTime(doc.createdAt)}</span>
              </span>
              <IconButton label={`Descargar ${doc.fileName}`} tone="accent" onClick={() => void downloadContractDoc(projectId, doc)}><DownloadIcon /></IconButton>
              {d.canDelete && (
                <IconButton label={`Borrar ${doc.fileName}`} tone="danger" disabled={busy}
                  onClick={() => { if (window.confirm(`¿Borrar ${doc.fileName}?`)) void run(() => api<ContractDocs>(`/contracts/${projectId}/documents/${doc.id}`, { method: 'DELETE' })); }}>
                  <TrashIcon />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
